import React, { useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import { User } from 'services/requests';
import { useInviteIraUser, useResendInviteIraUser } from 'hooks/api/iraUser';
import { useRole } from 'hooks/common/useRole';
import { useModalState } from 'hooks/modals';
import {
  useInviteIndependentIraUser,
  useResendInviteIndependentIraUser,
} from 'hooks/api/independentIra';
import { checkUser } from 'utils';
import { USER_TYPE } from 'constants/userTypes';

import { USER_STATUS } from 'constants/status';
import {
  EditIcon_24,
  LockIcon_24,
  MoreIcon_24,
  SendIcon_24,
  UnlockIcon_24,
  CheckIcon_24,
} from 'assets/icons';
import StatusUserLockModal from 'components/StatusUserLockModal';
import StatusUserUnlockModal from 'components/StatusUserUnlockModal';
import { showInfoToast } from 'components/Toast';
import { canEditProfile } from './TableActionMenu.statuses';

import messages from './TableActionMenu.messages';
import styles from './TableActionMenu.module.css';

type Props<T> = {
  user: T;
  onEdit?: (user: T) => void;
  onPrefetch?: (id: string) => void;
  userType: string;
};

const TableActionMenu = <T extends User>({
  user,
  onEdit,
  onPrefetch,
  userType,
}: Props<T>): JSX.Element | null => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  useOnClickOutside(ref, () => setIsOpen(false));
  const { isAdmin } = useRole();
  const {
    isModalVisible: isLockModalVisible,
    openModal: openLockModal,
    closeModal: closeLockModal,
  } = useModalState();
  const {
    payload: isCancelAutoLock,
    isModalVisible: isUnlockModalVisible,
    openModal: openUnlockModal,
    closeModal: closeUnlockModal,
  } = useModalState(false);

  const { mutateAsync: inviteIraUser } = useInviteIraUser();
  const {
    mutateAsync: inviteIndependentIraUser,
    isLoading: inviteIndependentIraUserLoading,
  } = useInviteIndependentIraUser();
  const { mutateAsync: resendInviteIraUser } = useResendInviteIraUser();
  const {
    mutateAsync: resendInviteIndependentIraUser,
    isLoading: resendInviteIndependentIraUserLoading,
  } = useResendInviteIndependentIraUser();

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  const onUserEditMouseEnter = () => {
    if (onPrefetch) onPrefetch(String(user.id));
  };

  const validStatuses: string[] = [USER_STATUS.draft, USER_STATUS.invited];
  const isInviteOption =
    validStatuses.includes(user.profileStatus) && !(user?.isLocked || user?.isAutoLocked);

  const InviteOption = (): JSX.Element | null => {
    const isResendInvite = user.profileStatus === USER_STATUS.invited;

    if (!isInviteOption) return null;
    if (!user.hasUserManager && user?.profileStatus === USER_STATUS.invited) return null;
    if (user?.custodianType === 'solera') return null;

    const getInviteOptionText = () => {
      if (user.hasUserManager) {
        return messages.notifyUserManager;
      }
      return messages.moveToInvited;
    };

    const onInvite = async (): Promise<void> => {
      if (checkUser(user))
        switch (userType) {
          case USER_TYPE.iraUser:
            const inviteIraResult = await inviteIraUser(user.id.toString());
            if (inviteIraResult.success) {
              if (user.hasUserManager) {
                showInfoToast(messages.successEmailToUserManager);
              } else {
                showInfoToast(messages.successMoveToInvited);
              }
            }
            return;
          case USER_TYPE.independentIra:
            const inviteIndependentIraResult = await inviteIndependentIraUser(
              user.id.toString(),
            );
            if (inviteIndependentIraResult.success) {
              if (user.hasUserManager) {
                showInfoToast(messages.successEmailToUserManager);
              } else {
                showInfoToast(messages.successMoveToInvited);
              }
            }
            return;
        }
    };

    const onResendInvite = async (): Promise<void> => {
      if (checkUser(user))
        switch (userType) {
          case USER_TYPE.iraUser:
            const resendInviteIraResult = await resendInviteIraUser(user.id.toString());
            if (resendInviteIraResult.success) {
              if (user.hasUserManager) {
                showInfoToast(messages.successEmailToUserManager);
              } else {
                showInfoToast(messages.successMoveToInvited);
              }
            }
            return;
          case USER_TYPE.independentIra:
            const resendInviteIndependentIraResult = await resendInviteIndependentIraUser(
              user.id.toString(),
            );
            if (resendInviteIndependentIraResult.success) {
              if (user.hasUserManager) {
                showInfoToast(messages.successEmailToUserManager);
              } else {
                showInfoToast(messages.successMoveToInvited);
              }
            }
            return;
        }
    };

    return (
      <div className={styles.option} onClick={isResendInvite ? onResendInvite : onInvite}>
        {!user.hasUserManager ? (
          <CheckIcon_24 className={styles.icon} />
        ) : (
          <SendIcon_24 className={styles.icon} />
        )}
        {getInviteOptionText()}
      </div>
    );
  };

  const isEditOption = canEditProfile(user.profileStatus, userType);
  const EditOption = (): JSX.Element | null => {
    if (!isEditOption) return null;

    const onUserEdit = () => {
      !!onEdit && onEdit(user);
      setIsOpen(false);
    };

    return (
      <div
        className={styles.option}
        onClick={onUserEdit}
        onMouseEnter={onUserEditMouseEnter}
      >
        <EditIcon_24 className={styles.icon} /> {messages.edit}
      </div>
    );
  };

  const isLockOption = user.profileStatus !== USER_STATUS.draft && isAdmin;
  const LockUnlockOption = (): JSX.Element | null => {
    if (!isLockOption) return null;

    const onLockClick = (): void => {
      openLockModal();
      setIsOpen(false);
    };

    const onUnlockClick = (isCancelAutoLock: boolean): void => {
      openUnlockModal(isCancelAutoLock);
      setIsOpen(false);
    };

    return (
      <React.Fragment>
        {(isInviteOption || isEditOption) && <div className={styles.divider} />}
        {user.isLocked ? (
          <div className={styles.option} onClick={() => onUnlockClick(false)}>
            <UnlockIcon_24 className={styles.icon} />
            {messages.unlock}
          </div>
        ) : (
          <div className={styles.option} onClick={onLockClick}>
            <LockIcon_24 className={styles.icon} />
            {user.isAutoLocked ? messages.manualLock : messages.lock}
          </div>
        )}

        {user.isAutoLocked && !user.isLocked && (
          <div className={styles.option} onClick={() => onUnlockClick(true)}>
            <UnlockIcon_24 className={styles.icon} />
            {messages.cancelAutolock}
          </div>
        )}
      </React.Fragment>
    );
  };

  if (!isInviteOption && !isEditOption && !LockUnlockOption()) return null;

  return (
    <div
      className={styles.actionWrapper}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <MoreIcon_24 className={styles.actionIcon} onClick={toggleIsOpen} />
      {isOpen && (
        <div className={styles.menuContainer} ref={ref}>
          <EditOption />
          <InviteOption />
          <LockUnlockOption />
        </div>
      )}

      <StatusUserLockModal
        onRequestClose={closeLockModal}
        isOpen={isLockModalVisible}
        user={user}
        userType={userType}
      />

      <StatusUserUnlockModal
        onRequestClose={closeUnlockModal}
        isOpen={isUnlockModalVisible}
        user={user}
        userType={userType}
        isCancelAutoLock={!!isCancelAutoLock}
      />
    </div>
  );
};

export default TableActionMenu;
